import get from 'lodash.get'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useState } from 'react'
import SydAutocomplete from '../../../commonDesign/SydAutocomplete'
import SydLabel from '../../../commonDesign/SydLabel'
import { usePipelineDataContextWithVariables } from '../provider/DDPDataProvider'
import { useFilterSelectContext } from './context'
import { getFilterValue } from './getFilterValue'

export const FilterSelector = ({
  label = '',
  description = '',
  filterLevel = 'account',
  pipeline,
  seriesAccessor = 'data',
  valueAccessor = 'orionEntityId',
  labelAccessor = 'levelName',
  style,
  className,
  variables,
  hideIfEmpty = false,
  size = 'sm',
  placeholder = 'Select',
  width = '100%',
  filterType,
  maxShow = 2,
  maxLabelLength = 18,
  ...selectProps
}) => {
  const { data, isLoading, isFetching } = usePipelineDataContextWithVariables(
    pipeline,
    variables
  )
  const [searchTerm, setSearchTerm] = useState('')
  const { selectedValues, setSelectedValues, selectedOption, setVariableValue } = useFilterSelectContext()

  const options = useMemo(() => {
    if (!data) return []
    const seriesData = get(data, seriesAccessor)
    if (!seriesData) return []
    return seriesData.map((item) => ({
      value: get(item, valueAccessor),
      label: get(item, labelAccessor) || get(item, valueAccessor)
    }))
  }, [data, labelAccessor, seriesAccessor, valueAccessor])

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options
    const lowerSearchTerm = searchTerm.toLowerCase()
    return options.filter((option) =>
      option.label.toLowerCase().includes(lowerSearchTerm)
    )
  }, [searchTerm, options])

  const setValue = useCallback(
    (val) => {
      setSelectedValues(val)
      setSearchTerm('')
      setVariableValue(
        getFilterValue(
          filterLevel,
          options.flatMap((option) => option.value),
          val.map((v) => v.value)
        )
      )
    },
    [setSelectedValues, setVariableValue, filterLevel, options]
  )

  if (filterType !== selectedOption) {
    return null
  }

  if (hideIfEmpty && !options?.length) {
    return null
  }

  return (
    <div style={{ width }}>
      <SydLabel label={label} description={description}>
        <SydAutocomplete
          loading={isLoading || isFetching}
          onChange={(e, val) => setValue(val)}
          extraStyles={style}
          className={className}
          value={selectedValues}
          defaultValue={selectedValues}
          options={filteredOptions ?? []}
          onInputChange={(e, val) => setSearchTerm(val)}
          placeholder={placeholder}
          size={size}
          multiple
          maxShow={maxShow}
          {...selectProps}
        />
      </SydLabel>
    </div>
  )
}

FilterSelector.propTypes = {
  label: PropTypes.string,
  description: PropTypes.string,
  applyTo: PropTypes.string,
  filterLevel: PropTypes.string,
  pipeline: PropTypes.string.isRequired,
  seriesAccessor: PropTypes.string,
  valueAccessor: PropTypes.string,
  labelAccessor: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  variables: PropTypes.object,
  hideIfEmpty: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 'sm', 'lg']),
  width: PropTypes.string,
  placeholder: PropTypes.string,
  maxShow: PropTypes.number,
  maxLabelLength: PropTypes.number,
  filterType: PropTypes.string
}
